import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "../hooks/apiRequest";
import { TicketProps, TaskProps, Priority, Severity } from "../types/interfaces";
import { ticketKeys } from "../utils/queryKeys";

interface TicketFilters {
  projectId?: string;
  assigneeId?: string;
  solved?: boolean;
  severity?: Severity;
  priority?: Priority;
}

function toQueryString(filters: TicketFilters): string {
  const params = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined) params.set(key, String(value));
  });
  return params.toString();
}

export function useTickets(filters: TicketFilters) {
  return useQuery({
    queryKey: ticketKeys.list({ ...filters, solved: filters.solved === undefined ? undefined : String(filters.solved) }),
    queryFn: async () => (await apiRequest<TicketProps[]>(`tickets?${toQueryString(filters)}`)).data,
  });
}

export function useTicket(id: string) {
  return useQuery({
    queryKey: ticketKeys.detail(id),
    queryFn: async () => (await apiRequest<TicketProps>(`tickets/${id}`)).data,
    enabled: !!id,
  });
}

export function useCreateTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: {
      projectId: string;
      subject: string;
      severity?: Severity;
      priority?: Priority;
      files?: File[];
    }) => {
      // multipart so PDFs, Word docs and images can travel with the ticket
      const formData = new FormData();
      formData.append("projectId", data.projectId);
      formData.append("subject", data.subject);
      if (data.severity) formData.append("severity", data.severity);
      if (data.priority) formData.append("priority", data.priority);
      (data.files || []).forEach((file) => formData.append("attachments", file));
      return (await apiRequest<TicketProps>("tickets", { method: "POST", body: formData })).data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ticketKeys.all }),
  });
}

export function useUpdateTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: string; data: { subject?: string; solved?: boolean; severity?: Severity; priority?: Priority; assigneeId?: string | null } }) =>
      (await apiRequest<TicketProps>(`tickets/${id}`, { method: "PUT", body: JSON.stringify(data) })).data,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ticketKeys.all });
      queryClient.invalidateQueries({ queryKey: ticketKeys.detail(variables.id) });
    },
  });
}

export function useDeleteTicket() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await apiRequest<void>(`tickets/${id}`, { method: "DELETE" })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ticketKeys.all }),
  });
}

export function useAddComment(ticketId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ comment, attachment }: { comment: string; attachment?: File | null }) => {
      const formData = new FormData();
      formData.append("comment", comment);
      if (attachment) formData.append("attachment", attachment);
      return (await apiRequest<TicketProps>(`tickets/${ticketId}/comments`, { method: "POST", body: formData })).data;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) }),
  });
}

export function usePromoteTicket(ticketId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async () =>
      (await apiRequest<{ ticket: TicketProps; task: TaskProps }>(`tickets/${ticketId}/promote`, { method: "POST" })).data,
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ticketKeys.detail(ticketId) }),
  });
}
